import type { VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../styles/lib/utils';
import { Button, type buttonVariants, IconButton } from '../Button';
import { ButtonGroup } from '../ButtonGroup';
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from '../DropdownMenu';
import { Text } from '../Text';

export type SplitButtonAction = {
  value: string;
  label: React.ReactNode;
  /** Optional muted second line shown under the label in the menu. */
  description?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  testId?: string;
};

export type SplitButtonProps = VariantProps<typeof buttonVariants> & {
  actions: SplitButtonAction[];
  /** Uncontrolled initial selection; falls back to the first action. */
  defaultValue?: string;
  /** Controlled selection; pair with onValueChange. */
  value?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  loading?: boolean;
  align?: 'start' | 'end';
  className?: string;
  /** Extra classes for both button segments (e.g. toolbar height overrides). */
  buttonClassName?: string;
  'data-testid'?: string;
};

/** Resolve the active action: the one matching value, else the first action. */
export function getSelectedAction(
  actions: SplitButtonAction[],
  value: string | undefined,
): SplitButtonAction | undefined {
  return actions.find((action) => action.value === value) ?? actions[0];
}

/**
 * GitHub-style split button: the main button executes the currently selected
 * action; the attached chevron opens a menu where clicking an item selects it
 * (checkmark) without executing.
 */
function SplitButton({
  actions,
  defaultValue,
  value,
  onValueChange,
  variant = 'secondary',
  size,
  disabled = false,
  loading = false,
  align = 'end',
  className,
  buttonClassName,
  'data-testid': testId,
}: SplitButtonProps) {
  const [internalValue, setInternalValue] = React.useState(defaultValue);
  const selectedValue = value ?? internalValue;
  const selectedAction = getSelectedAction(actions, selectedValue);

  const handleSelect = (actionValue: string) => {
    if (value === undefined) setInternalValue(actionValue);
    onValueChange?.(actionValue);
  };

  if (!selectedAction) return null;

  return (
    <ButtonGroup data-slot="split-button" className={className}>
      <Button
        variant={variant}
        size={size}
        disabled={disabled || selectedAction.disabled}
        loading={loading}
        onClick={selectedAction.onClick}
        className={buttonClassName}
        data-testid={testId}
      >
        {selectedAction.label}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <IconButton
            icon="chevronDown"
            variant={variant}
            size="icon"
            disabled={disabled}
            aria-label="More actions"
            className={buttonClassName}
            data-testid={testId ? `${testId}-menu-trigger` : undefined}
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent align={align} className="min-w-44 max-w-72">
          {actions.map((action) => (
            <DropdownMenuCheckboxItem
              key={action.value}
              checked={action.value === selectedAction.value}
              disabled={action.disabled}
              onSelect={() => handleSelect(action.value)}
              className={cn(action.description && 'items-start')}
              data-testid={action.testId}
            >
              <div className="flex flex-col">
                <Text className="text-xs text-primary-dark">{action.label}</Text>
                {action.description && <Text className="text-2xs text-grey-50">{action.description}</Text>}
              </div>
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </ButtonGroup>
  );
}

export { SplitButton };
